import React, {useState} from 'react';
import {Button, Card, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Space, Spin} from 'antd';
import {useHistory, useParams} from 'react-router-dom';
import {useMount, useRequest} from 'ahooks';
import PageHeaderLayout from '../../layout/pageHeaderLayout';
import Upload from '../../components/Upload';
import AreaSelect from '../../components/AreaSelect';
import DatePicker from '../../components/DatePicker';
import BaseDataSelect from '../../components/BaseDataSelect';
import Select from '../../components/Select';
import DictSelect from '../../components/DictSelect';
import * as api from '../../apis/preDealer';
import {useSelector} from 'react-redux';
import {RootState} from '../../models';
import styles from './index.module.css';

interface RouteParams { id: string }
const MAX_FILE_SIZE = 20 * 1024 * 1024;

type PositionSelectProps = Omit<React.ComponentProps<typeof Select>, 'url' | 'getData' | 'textField' | 'valueField'> & {
    type: number;
};

function PositionSelect({type, allowClear = false, ...props}: PositionSelectProps) {
    return <Select {...props} allowClear={allowClear} url={`/position/dict?type=${type}`}
                   getData={data => data.data.map((item: any) => ({...item, displayName: `${item.name}(${item.user})`}))}
                   textField="displayName" valueField="id"/>;
}

function PreDealerDetail() {
    const {id} = useParams<RouteParams>();
    const history = useHistory();
    const [form] = Form.useForm();
    const [detail, setDetail] = useState<any>();
    const [licenses, setLicenses] = useState<any[]>([]);
    const invoiceType = Form.useWatch('invoiceType', form);
    const qualification2 = Form.useWatch('qualification2', form);
    const qualification3 = Form.useWatch('qualification3', form);
    const dealerFieldPermissions = useSelector((state: RootState) =>
        state.auth.routes['/customer/dealer/detail/:id?']?.resources || []);
    const viewTemporaryPeriod = dealerFieldPermissions.includes('fieldTemporaryPeriodView');
    const editTemporaryPeriod = dealerFieldPermissions.includes('fieldTemporaryPeriodEdit');
    const viewTemporaryCreditLimit = dealerFieldPermissions.includes('fieldTemporaryCreditLimitView');
    const editTemporaryCreditLimit = dealerFieldPermissions.includes('fieldTemporaryCreditLimitEdit');

    const {run: loadData, loading} = useRequest(async () => {
        const [detailResponse, optionsResponse] = await Promise.all([
            api.getPreDealer(Number(id)), api.getPublicOptions()
        ]);
        const result = detailResponse.data.data;
        const licenseOptions = optionsResponse.data.data.licenses;
        const submitted = new Map((result.content.licenses || []).map((item: any) => [item.licenseId, item]));
        form.setFieldsValue({
            ...result.content,
            licenses: licenseOptions.map((item: any) => ({licenseId: item.id, ...(submitted.get(item.id) || {})}))
        });
        setLicenses(licenseOptions);
        setDetail(result);
    }, {manual: true});

    useMount(loadData);

    const licenseRequired = (license: any) => {
        if (license.id === 2) return qualification2 === true;
        if (license.id === 3) return qualification3 === true;
        return license.required;
    };

    const {runAsync: convert, loading: converting} = useRequest(async (data: any) => {
        data.licenses = await Promise.all(data.licenses.map(async (item: any) => {
            let path = item.path;
            if (path?.originFileObj) path = await api.uploadPublicFile(path.originFileObj);
            return {licenseId: item.licenseId, path, expire: item.expire};
        }));
        const response = await api.convertToDealer(Number(id), data);
        message.success('客户生成成功');
        history.replace(`/customer/dealer/version/${response.data.data}`);
    }, {manual: true});

    async function confirmConvert() {
        try {
            const data = await form.validateFields();
            if ((data.shipping || []).filter((item: any) => item.default).length !== 1) {
                message.info('必须且只能设置一个默认收货地址');
                return;
            }
            if ((data.salesName && !data.salesPhone) || (!data.salesName && data.salesPhone)) {
                message.info('采购员姓名和手机必须同时填写');
                return;
            }
            Modal.confirm({
                title: '是否确认创建客户？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => convert(data),
            });
        } catch {
            // 表单组件会展示具体的校验错误。
        }
    }

    return <PageHeaderLayout canBack withFooterBar onOk={confirmConvert} btnLoading={converting}
                             okBtnText="生成客户" onCancel={() => history.goBack()}>
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} className={styles.container}>
                <Card title="基本信息" extra={detail && `申请编号：${detail.applicationNo}　客户选择的销售人员：${detail.selectedSalesName}`}>
                    <Form labelCol={{span: 7}} form={form}>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="name" label="客户名称" rules={[{required: true, whitespace: true}]}><Input onChange={event => form.setFieldValue('invoiceName', event.target.value)}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="nameShort" label="客户简称"><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="dealerGroup" label="客户组" rules={[{required: true}]}><BaseDataSelect name="dealerGroups" appendNull={false}/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="group" label="集团"><BaseDataSelect name="groups"/></Form.Item></Col>
                            <Col span={8}><Form.Item name="area" label="销售地区" rules={[{required: true}]}><BaseDataSelect name="saleAreas" appendNull={false}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="country" label="国家" rules={[{required: true}]}><BaseDataSelect name="countries" appendNull={false} defaultSelectFirst/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="currency" label="币种" rules={[{required: true}]} initialValue="CNY"><BaseDataSelect name="currencies" appendNull={false}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="property" label="客户性质" rules={[{required: true}]}><BaseDataSelect name="dealerNatures" appendNull={false}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="zipCode" label="客户邮编"><Input/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="businessUser" label="商务人员" rules={[{required: true}]}><PositionSelect type={1}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="salesUser" label="销售人员" rules={[{required: true}]}><PositionSelect type={0}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="salesUser2" label="销售人员2"><PositionSelect type={0} allowClear/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="legalRepresentative" label="法人代表"><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="registeredCapital" label="注册资金"><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="registeredDate" label="成立日期"><DatePicker style={{width: '100%'}}/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="manager" label="总经理姓名" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="managerPhone" label="总经理电话" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="paymentMethod" label="付款方式" initialValue="电汇"><DictSelect dict="PaymentMethod" appendNull={false}/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="creditLimit" label="信用额度"><InputNumber min={0} precision={2} addonAfter="万元"/></Form.Item></Col>
                            <Col span={viewTemporaryCreditLimit ? 8 : 0} style={viewTemporaryCreditLimit ? {} : {display: 'none'}}><Form.Item name="temporaryCreditLimit" label="临时信用额度"><InputNumber min={0} precision={2} addonAfter="万元" disabled={!editTemporaryCreditLimit}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="period" label="账期"><Select url="/dict/?typeName=DealerPeriod" getData={data => data.data} textField="value" valueField="value" suffixIcon="天"/></Form.Item></Col>
                            <Col span={viewTemporaryPeriod ? 8 : 0} style={viewTemporaryPeriod ? {} : {display: 'none'}}><Form.Item name="temporaryPeriod" label="临时账期"><Select allowClear url="/dict/?typeName=DealerPeriod" getData={data => data.data} textField="value" valueField="value" suffixIcon="天" disabled={!editTemporaryPeriod}/></Form.Item></Col>
                        </Row>
                        <Row><Col span={8}><Form.Item name="reason" label="申请理由"><Input.TextArea/></Form.Item></Col></Row>
                        <Row gutter={16}>
                            <Col span={5}><Form.Item name="includeTax" valuePropName="checked" initialValue={true}><Checkbox disabled>是否含税</Checkbox></Form.Item></Col>
                            <Col span={5}><Form.Item name="specialBilling" valuePropName="checked" initialValue={false}><Checkbox>特殊开票</Checkbox></Form.Item></Col>
                            <Col span={5}><Form.Item name="autoWriteOff" valuePropName="checked" initialValue={false}><Checkbox>付款单自动核销</Checkbox></Form.Item></Col>
                            <Col span={4}><Form.Item name="qualification2" valuePropName="checked"><Checkbox>2类资质</Checkbox></Form.Item></Col>
                            <Col span={5}><Form.Item name="qualification3" valuePropName="checked"><Checkbox>3类资质/其他</Checkbox></Form.Item></Col>
                        </Row>
                    </Form>
                </Card>

                <Card title="收货地址">
                    <Form form={form}>
                        <Row gutter={10} className={styles.tableHeader}><Col span={4} offset={2}>收货人</Col><Col span={4}>手机</Col><Col span={3}>省</Col><Col span={3}>市</Col><Col span={4}>详细地址</Col><Col span={4}>默认地址</Col></Row>
                        <Form.List name="shipping">{(fields, {add, remove}) => fields.map(field => <Row gutter={10} key={field.key}>
                            <Col span={2}><Space><Button onClick={() => add()}>+</Button><Button onClick={() => fields.length > 1 && remove(field.name)}>-</Button></Space></Col>
                            <Col span={4}><Form.Item name={[field.name, 'name']} rules={[{required: true, message: '请输入收货人'}]}><Input/></Form.Item></Col>
                            <Col span={4}><Form.Item name={[field.name, 'phone']} rules={[{required: true, message: '请输入手机'}]}><Input/></Form.Item></Col>
                            <Col span={3}><Form.Item name={[field.name, 'provinceId']} rules={[{required: true, message: '请选择省份'}]}><AreaSelect onChange={() => form.setFieldValue(['shipping', field.name, 'cityId'], undefined)}/></Form.Item></Col>
                            <Col span={3}><Form.Item noStyle dependencies={[['shipping', field.name, 'provinceId']]}>{({getFieldValue}) => <Form.Item name={[field.name, 'cityId']} rules={[{required: true, message: '请选择城市'}]}><AreaSelect type="city" parent={getFieldValue(['shipping', field.name, 'provinceId'])}/></Form.Item>}</Form.Item></Col>
                            <Col span={4}><Form.Item name={[field.name, 'address']} rules={[{required: true, message: '请输入详细地址'}]}><Input/></Form.Item></Col>
                            <Col span={4}><Form.Item name={[field.name, 'default']} valuePropName="checked"><Checkbox onChange={event => {const rows = form.getFieldValue('shipping'); form.setFieldValue('shipping', rows.map((item: any, index: number) => ({...item, default: index === field.name ? event.target.checked : false})));}}/></Form.Item></Col>
                        </Row>)}</Form.List>
                    </Form>
                </Card>

                <Card title="开票信息">
                    <Form labelCol={{span: 7}} form={form}>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="invoiceName" label="名称" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="invoiceType" label="发票类型" rules={[{required: true}]}><DictSelect dict="InvoiceType" appendNull={false} onChange={value => form.setFieldValue('salesTaxGroup', value.startsWith('special') ? 'All-专票' : 'All-普票')}/></Form.Item></Col>
                            <Col span={8}><Form.Item name="taxNo" label="税号" rules={[{required: true}]}><Input/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="bankName" label="开户银行" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="bankNo" label="银行账户" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="phone" label="电话号码" rules={[{required: true}]}><Input/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="address" label="开票地址" rules={[{required: true}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="invoiceEmail" label="电子邮箱" rules={[{required: true, type: 'email'}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="salesTaxGroup" label="销售税组" rules={[{required: true}]} initialValue="All-专票"><BaseDataSelect name="salesTaxGroups" appendNull={false}/></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}><Form.Item name="invoiceAddress" label="发票邮寄地址" rules={[{required: ['special', 'ordinary'].includes(invoiceType)}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="invoiceRecipient" label="发票邮寄联系人" rules={[{required: ['special', 'ordinary'].includes(invoiceType)}]}><Input/></Form.Item></Col>
                            <Col span={8}><Form.Item name="invoicePhone" label="发票邮寄联系电话" rules={[{required: ['special', 'ordinary'].includes(invoiceType)}]}><Input/></Form.Item></Col>
                        </Row>
                    </Form>
                </Card>

                <Card title="资质文件" extra="注：附件最大20M，支持格式：PDF、Word、Excel、Txt、JPG、PNG、BMP、GIF、RAR、ZIP">
                    <Form labelCol={{span: 9}} form={form}><Form.List name="licenses">{() => licenses.map((item, index) => <Row key={item.id} gutter={16}>
                        <Form.Item name={[index, 'licenseId']} hidden><Input/></Form.Item>
                        <Col span={12}><Form.Item name={[index, 'path']} label={item.name} rules={[{required: licenseRequired(item)}, {validator: Upload.validator({limit: MAX_FILE_SIZE})}]}><Upload maxCount={1}/></Form.Item></Col>
                        <Col span={12}><Form.Item name={[index, 'expire']} label="有效期" rules={[{required: licenseRequired(item) && item.requiredExpire}]}><DatePicker.RangePicker/></Form.Item></Col>
                    </Row>)}</Form.List></Form>
                </Card>

                <Card title="登录账号" extra="管理员账户必填；采购员账号为选填，填写时姓名和手机需成对填写">
                    <Form form={form}>
                        <Row gutter={10} className={styles.tableHeader}><Col span={8}>角色</Col><Col span={8}>姓名</Col><Col span={8}>手机</Col></Row>
                        <Row gutter={10}><Col span={8}>管理员账户</Col><Col span={8}><Form.Item name="adminName" rules={[{required: true, message: '请输入管理员姓名'}]}><Input/></Form.Item></Col><Col span={8}><Form.Item name="adminPhone" rules={[{required: true, message: '请输入管理员手机'}]}><Input/></Form.Item></Col></Row>
                        <Row gutter={10}><Col span={8}>采购员账号</Col><Col span={8}><Form.Item name="salesName"><Input/></Form.Item></Col><Col span={8}><Form.Item name="salesPhone"><Input/></Form.Item></Col></Row>
                    </Form>
                </Card>
            </Space>
        </Spin>
    </PageHeaderLayout>;
}

export default PreDealerDetail;
